import React, { useState, useEffect } from 'react';
import { API, fetchRoles as utilFetchRoles } from '../utils/api';
import axios from 'axios';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';

// SortIcon component moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-gray-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 12l5-5 5 5H5z"/>
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M15 8l-5 5-5-5h10z"/>
    </svg>
  );
};

const RoleManagement = ({ roles, fetchRoles }) => {
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [sortField, setSortField] = useState('role_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [newRole, setNewRole] = useState({
    role_name: '', description: ''
  });
  const [editRole, setEditRole] = useState({
    role_name: '', description: ''
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedRoles = () => {
    const filteredRoles = roles.filter(role => 
      role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    return filteredRoles.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/roles`, newRole);
      setNewRole({ role_name: '', description: '' });
      setShowRoleForm(false);
      fetchRoles();
      setSuccessModal({
        show: true,
        message: 'Role created successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to create role: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setEditRole({
      role_name: role.role_name,
      description: role.description || ''
    });
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      const updateData = {};
      if (editRole.role_name !== editingRole.role_name) updateData.role_name = editRole.role_name;
      if (editRole.description !== (editingRole.description || '')) updateData.description = editRole.description;

      await axios.put(`${API}/admin/roles/${editingRole.id}`, updateData);
      setEditingRole(null);
      setEditRole({ role_name: '', description: '' });
      fetchRoles();
      setSuccessModal({
        show: true,
        message: 'Role updated successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to update role: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const cancelEditRole = () => {
    setEditingRole(null);
    setEditRole({ role_name: '', description: '' });
  };

  const handleDeleteRole = (roleId) => {
    setConfirmModal({
      show: true,
      message: 'Are you sure you want to delete this role?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/roles/${roleId}`);
          fetchRoles();
          setConfirmModal({ show: false, message: '', onConfirm: null });
          setSuccessModal({
            show: true,
            message: 'Role deleted successfully!'
          });
        } catch (error) {
          setConfirmModal({ show: false, message: '', onConfirm: null });
          setErrorModal({
            show: true,
            message: 'Failed to delete role: ' + (error.response?.data?.detail || 'Unknown error')
          });
        }
      }
    });
  };

  return (
    <>
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700 bg-slate-700 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">All Roles</h2>
            <button
              onClick={() => setShowRoleForm(!showRoleForm)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-500 flex items-center transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"></path>
              </svg>
              {showRoleForm ? 'Cancel' : 'Add Role'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-3 border-b border-slate-700">
          <div className="flex items-center">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            {searchTerm && (
              <div className="ml-3 text-sm text-gray-400">
                {getSortedRoles().length} of {roles.length} roles
              </div>
            )}
          </div>
        </div>

        {showRoleForm && (
          <div className="px-6 py-4 border-b border-slate-700 bg-slate-700/50">
            <form onSubmit={handleCreateRole} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Role Name"
                value={newRole.role_name}
                onChange={(e) => setNewRole({...newRole, role_name: e.target.value})}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <div className="md:col-span-2">
                <textarea
                  placeholder="Description (optional)"
                  value={newRole.description}
                  onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              <button type="submit" className="md:col-span-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-500 transition-colors">
                Create Role
              </button>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-600 bg-slate-700">
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('role_name')}
                >
                  <div className="flex items-center">
                    ROLE NAME
                    <SortIcon field="role_name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">DESCRIPTION</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-400 text-sm">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {getSortedRoles().map((role, index) => (
                <tr key={role.id} className={`hover:bg-slate-700/50 transition-colors ${index !== getSortedRoles().length - 1 ? "border-b border-slate-700/50" : ""}`}>
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <span className="font-medium text-white">{role.role_name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-gray-300">{role.description || <span className="text-gray-500 italic">No description</span>}</span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-purple-300 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 mr-2 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role.id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-red-300 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 transition-colors"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {getSortedRoles().length === 0 && (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-gray-500">
                    {searchTerm ? `No roles found matching "${searchTerm}"` : 'No roles found. Create a new role to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Role Modal */}
      {editingRole && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 rounded-t-2xl">
              <h3 className="text-lg font-bold text-white">Edit Role: {editingRole.role_name}</h3>
            </div>
            <form onSubmit={handleUpdateRole} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role Name</label>
                <input
                  type="text"
                  value={editRole.role_name}
                  onChange={(e) => setEditRole({...editRole, role_name: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={editRole.description}
                  onChange={(e) => setEditRole({...editRole, description: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={cancelEditRole}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 transition-all"
                >
                  Update Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </>
  );
};

export default RoleManagement;
